import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { MOCK_SKUS } from '@/screens/ordering/mock-data'
import { useOrderingStore } from '@/screens/ordering/store'
import { nextValidDeliveryISO } from '@/screens/ordering/validation'

/**
 * Seeds the ordering store imperatively from a startParams record.
 * Called by the unguided player after `resetAll()` to restore the initial cart
 * state declared in the sub-module's `startParams`.
 */
export function applyCartStartParams(params: Record<string, string>) {
  const seedCount = params['cart']
  const deselect = params['deselect']
  if (!seedCount) return
  const n = Number(seedCount)
  if (!Number.isInteger(n) || n < 0 || n > MOCK_SKUS.length) return
  const { clear, addLine } = useOrderingStore.getState()
  const deliveryDate = nextValidDeliveryISO()
  clear()
  for (const sku of MOCK_SKUS.slice(0, n)) addLine(sku, 4, deliveryDate)
  if (deselect === '1') {
    const { lines, toggleSelected } = useOrderingStore.getState()
    for (const line of lines) if (line.selected) toggleSelected(line.lineId)
  }
}

/**
 * Seeds the ordering store from the `?cart=N` URL param used by guided lessons.
 * Safe to call from any screen — the seeding only fires once per step+count key
 * so it is idempotent and won't re-run on unrelated re-renders.
 *
 * The optional `?deselect=1` param deselects all seeded lines after seeding.
 */
export function useCartSeed() {
  const [searchParams] = useSearchParams()
  const stepKey = searchParams.get('step') ?? ''
  const seedCount = searchParams.get('cart')
  const deselect = searchParams.get('deselect')

  const addLine = useOrderingStore((s) => s.addLine)
  const clear = useOrderingStore((s) => s.clear)
  const seededFor = useRef<string | null>(null)

  useEffect(() => {
    if (seedCount === null) return
    const key = `${stepKey}|${seedCount}`
    if (seededFor.current === key) return
    const n = Number(seedCount)
    if (!Number.isInteger(n) || n < 0 || n > MOCK_SKUS.length) return
    seededFor.current = key
    const deliveryDate = nextValidDeliveryISO()
    clear()
    for (const sku of MOCK_SKUS.slice(0, n)) addLine(sku, 4, deliveryDate)
    if (deselect === '1') {
      const { lines: seeded, toggleSelected: toggle } = useOrderingStore.getState()
      for (const line of seeded) if (line.selected) toggle(line.lineId)
    }
  }, [seedCount, stepKey, deselect, clear, addLine])
}
